import React, { useCallback, useMemo } from 'react';

import { MoreButtonCDS } from '@sharedCR/coreDesignSystem/Buttons';
import { AddBoxIcon, DeleteIcon } from '@sharedCR/coreDesignSystem/icons';
import MenuPopoverCDS from '@sharedCR/coreDesignSystem/MenuPopoverCDS';
import { noop } from 'lodash';
import PropTypes from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';
import usePopover from 'hooks/usePopover';
import { getConditionalArray } from 'utils/data';

const defaultObject = {};

const ActionButton = ({
  isSingleOption = false,
  onSingleOptionDelete = noop,
  isParent: isParentFlow = false,
  childIndex = 0,
  parentIndex = 0,
  setSelectedParentIndex = noop,
  parentId = '',
  form = defaultObject,
  setForm = noop,
  setIsTouched = noop,
  rootDependenceGroupId = '',
  dependenceGroupId = '',
}) => {
  const { anchorEl, onSetAnchorElement, onClearAnchorElement } = usePopover();

  const onClickMenu = useCallback(
    e => {
      if (isParentFlow) {
        setSelectedParentIndex(parentIndex);
      }

      onSetAnchorElement(e);
    },
    [parentIndex, onSetAnchorElement, setSelectedParentIndex, isParentFlow],
  );

  const onClickAddNewChild = useCallback(() => {
    const updatedForm = {
      ...form,
      parents: form.parents.map((parent, index) => {
        if (index === parentIndex) {
          return {
            ...parent,
            children: [
              ...parent.children,
              {
                dependenceGroupParentId: parentId,
                dependenceGroupId: dependenceGroupId || crypto.randomUUID(),
              },
            ],
          };
        }

        return parent;
      }),
    };

    setForm(updatedForm);
  }, [form, setForm, parentIndex, parentId, dependenceGroupId]);

  const onClickAddNewParent = useCallback(() => {
    const updatedForm = {
      ...form,
      parents: [
        ...form.parents,
        {
          id: crypto.randomUUID(),
          children: [],
          rootDependenceGroupId,
          dependenceGroupId: rootDependenceGroupId,
        },
      ],
    };

    setForm(updatedForm);
    setTimeout(() => {
      setSelectedParentIndex(updatedForm.parents.length - 1);
    }, 0);
  }, [form, setForm, rootDependenceGroupId, setSelectedParentIndex]);

  const onClickDelete = useCallback(() => {
    const updatedForm = {
      ...form,
      parents: isParentFlow
        ? form.parents.filter((_, index) => index !== parentIndex)
        : form.parents.map((parent, index) => {
            if (index === parentIndex) {
              return {
                ...parent,
                children: parent.children.filter((_, index) => index !== childIndex),
              };
            }

            return parent;
          }),
    };

    if (isParentFlow) {
      setSelectedParentIndex(0);
    }

    setForm(updatedForm);
    setIsTouched(true);
  }, [isParentFlow, form, parentIndex, setForm, setIsTouched, childIndex, setSelectedParentIndex]);

  const items = useMemo(
    () => [
      ...getConditionalArray(isParentFlow, [
        {
          text: 'Add Dependence',
          onClick: onClickAddNewChild,
          icon: <AddBoxIcon size={IconSize[20]} />,
        },
      ]),
      ...getConditionalArray(!isSingleOption, [
        {
          text: 'Add New',
          onClick: isParentFlow ? onClickAddNewParent : onClickAddNewChild,
          icon: <AddBoxIcon size={IconSize[20]} />,
        },
      ]),
      {
        text: 'Delete',
        onClick: isSingleOption ? onSingleOptionDelete : onClickDelete,
        icon: <DeleteIcon size={IconSize[20]} />,
      },
    ],
    [
      isParentFlow,
      onClickAddNewChild,
      onClickAddNewParent,
      onClickDelete,
      isSingleOption,
      onSingleOptionDelete,
    ],
  );

  return (
    <>
      <MoreButtonCDS isSelected={!!anchorEl} onClick={onClickMenu} />

      <MenuPopoverCDS anchorEl={anchorEl} items={items} onClose={onClearAnchorElement} />
    </>
  );
};

ActionButton.propTypes = {
  isParent: PropTypes.bool,
  childIndex: PropTypes.number,
  parentIndex: PropTypes.number,
  setSelectedParentIndex: PropTypes.func,
  parentId: PropTypes.string,
  form: PropTypes.object,
  setForm: PropTypes.func,
  isSingleOption: PropTypes.bool,
  dependenceGroupId: PropTypes.string,
  rootDependenceGroupId: PropTypes.string,
  setIsTouched: PropTypes.func,
  onSingleOptionDelete: PropTypes.func,
};

export default ActionButton;
